// =============================================================================
// Peças compartilhadas do design system CF
//
// Regra do CLAUDE.md §3.6 que orienta este arquivo: nem tudo é card. Borda,
// preenchimento, raio e sombra dizem "objeto separado" — gastar os quatro em
// todo bloco achata a hierarquia. Aqui só o KPI e o painel de gráfico têm
// moldura; tabela e lista vivem sobre linhas.
// =============================================================================

import { useId, useState, type ReactNode } from "react";
import type { Tom } from "../lib/formato.ts";

const TOM_CLASSE: Record<Tom, string> = {
  ok: "bg-ok-bg text-ok-tinta border-ok-linha",
  atencao: "bg-atencao-bg text-atencao-tinta border-atencao-linha",
  risco: "bg-risco-bg text-risco-tinta border-risco-linha",
  neutro: "bg-neutro-bg text-neutro-tinta border-neutro-linha",
};

export function Pilula({ tom, children }: { tom: Tom; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${TOM_CLASSE[tom]}`}
    >
      <span aria-hidden className="size-1.5 rounded-full bg-current" />
      {children}
    </span>
  );
}

/**
 * Explicação da métrica, ao lado do título do gráfico.
 *
 * Exigência do §3.6: todo gráfico diz o que a métrica significa. Num painel
 * onde "comprometido" e "provisionado" são coisas diferentes e parecidas, quem
 * abre a tela uma vez por mês não lembra a diferença.
 */
export function Dica({ texto }: { texto: string }) {
  const [aberto, setAberto] = useState(false);
  const id = useId();
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-label="O que este número significa"
        aria-expanded={aberto}
        aria-describedby={aberto ? id : undefined}
        onClick={() => setAberto((v) => !v)}
        onBlur={() => setAberto(false)}
        className="grid size-4 place-items-center rounded-full border border-linha-forte text-[10px] font-bold text-tinta-fraca hover:border-laranja hover:text-laranja-tinta"
      >
        ?
      </button>
      {aberto && (
        <span
          id={id}
          role="tooltip"
          className="absolute top-6 left-0 z-20 w-64 rounded-lg border border-linha bg-superficie p-3 text-[12.5px] leading-snug font-normal text-tinta-media shadow-lg"
        >
          {texto}
        </span>
      )}
    </span>
  );
}

export function CabecalhoGrafico({
  titulo,
  dica,
  acessorio,
}: {
  titulo: string;
  dica?: string;
  acessorio?: ReactNode;
}) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3">
      <h2 className="flex items-center gap-2 font-display text-[16px] font-bold tracking-tight">
        {titulo}
        {dica && <Dica texto={dica} />}
      </h2>
      {acessorio}
    </div>
  );
}

export function Painel({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    // `.surface-card` é o cartão do design system CF: 12px de raio e sombra
    // DUPLA — uma rasa que assenta, outra larga que dá profundidade. Ele estava
    // escrito em `styles.css` e nenhum componente o usava; os painéis vinham
    // com o raio de 4px do Tailwind e uma sombra só, que é o "div com borda
    // dura" que o próprio comentário do design system diz para não fazer.
    //
    // O respiro é o do protótipo (`.card`: 22px em cima, 24px nos lados).
    <section className={`surface-card px-6 py-[22px] ${className}`}>
      {children}
    </section>
  );
}

/**
 * Estado vazio instrutivo (§3.6): diz por que está vazio e o que fazer, nunca
 * só "sem dados". Num sistema que acabou de entrar em produção, metade das
 * telas começa vazia e o usuário precisa saber se é erro ou se é o esperado.
 */
export function Vazio({ titulo, dica }: { titulo: string; dica?: string }) {
  return (
    <div className="grid place-items-center gap-1 rounded-lg border border-dashed border-linha px-4 py-10 text-center">
      <p className="text-[13.5px] font-semibold text-tinta-media">{titulo}</p>
      {dica && <p className="max-w-sm text-[12.5px] text-tinta-fraca">{dica}</p>}
    </div>
  );
}

/** Cor da borda esquerda e do ponto de status — a anatomia do cartão do
 *  protótipo: o estado se lê antes do número, pela cor, sem ter de comparar
 *  dois valores. Sem `tom`, fica o azul médio, que é "sem juízo de valor". */
const TOM_BORDA: Record<Tom, string> = {
  ok: "border-l-ok-tinta",
  atencao: "border-l-atencao-tinta",
  risco: "border-l-risco-tinta",
  neutro: "border-l-azul-medio",
};
/** ⚠️ O protótipo usa DUAS anatomias de cartão: borda à esquerda de 4px no
 *  painel de Despesa, e borda em cima de 3px no Painel do Gestor. Não é
 *  descuido dele — são telas diferentes, e manter a distinção é o que "seguir
 *  o protótipo" quer dizer aqui. */
const TOM_BORDA_TOPO: Record<Tom, string> = {
  ok: "border-t-ok-tinta",
  atencao: "border-t-atencao-tinta",
  risco: "border-t-risco-tinta",
  neutro: "border-t-azul-medio",
};
const TOM_PONTO: Record<Tom, string> = {
  ok: "bg-ok-tinta",
  atencao: "bg-atencao-tinta",
  risco: "bg-risco-tinta",
  neutro: "bg-azul-medio",
};

/** Uma linha de comparação no pé do cartão: "vs mês anterior ▲ 3,2%". */
export type Comparacao = { rotulo: string; valor: ReactNode };

/**
 * Cartão de indicador.
 *
 * Segue o protótipo (`.kpi`): rótulo em caixa alta, ponto de status, valor
 * grande e até duas linhas de comparação no pé. A comparação é o que o
 * protótipo acrescenta e faltava aqui — número sozinho não diz se está bom, e
 * era o que o Financeiro apontou ao ver a tela pela primeira vez.
 */
export function Kpi({
  rotulo,
  valor,
  apoio,
  dica,
  destaque = false,
  tom,
  comparacoes,
  borda = "esquerda",
}: {
  rotulo: string;
  valor: string;
  apoio?: string;
  dica?: string;
  destaque?: boolean;
  tom?: Tom;
  comparacoes?: Comparacao[];
  /** "esquerda" na Despesa (`.kpi` do protótipo), "topo" no Painel do Gestor. */
  borda?: "esquerda" | "topo";
}) {
  const corValor =
    tom === "risco" ? "text-risco-tinta" : tom === "ok" ? "text-ok-tinta" : "text-tinta";
  // `destaque` continua ganhando do tom: é o cartão que a tela elegeu como o
  // principal, e trocar a borda dele por estado o faria sumir no meio dos outros.
  const noTopo = borda === "topo";
  const cor = noTopo
    ? destaque
      ? "border-t-laranja"
      : TOM_BORDA_TOPO[tom ?? "neutro"]
    : destaque
      ? "border-l-laranja"
      : TOM_BORDA[tom ?? "neutro"];
  const espessura = noTopo ? "border-t-[3px]" : "border-l-4";

  return (
    <div
      className={`rounded-xl border border-linha bg-superficie px-[18px] py-4 shadow-[0_1px_2px_rgb(16_40_80_/_0.04)] ${espessura} ${cor}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase">
            {rotulo}
          </span>
          {dica && <Dica texto={dica} />}
        </div>
        {tom && (
          <span aria-hidden className={`mt-1 size-[9px] shrink-0 rounded-full ${TOM_PONTO[tom]}`} />
        )}
      </div>
      <div className={`num mt-1.5 font-display text-[21px] leading-none font-bold ${corValor}`}>
        {valor}
      </div>
      {apoio && <div className="num mt-1.5 text-[12px] text-tinta-fraca">{apoio}</div>}
      {comparacoes && comparacoes.length > 0 && (
        <dl className="mt-2 space-y-0.5">
          {comparacoes.map((c) => (
            <div key={c.rotulo} className="flex items-baseline justify-between gap-2 text-[11px]">
              <dt className="truncate text-tinta-fraca">{c.rotulo}</dt>
              <dd className="num shrink-0 font-semibold text-tinta-media">{c.valor}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

/**
 * Variação percentual, com seta e cor.
 *
 * ⚠️ `favoravelSeSobe` existe porque em despesa subir é ruim e em receita é
 * bom — pintar toda alta de vermelho faria o Financeiro ler "estouro" onde há
 * receita crescendo. Abaixo de 1% em módulo não vira seta: ruído com seta
 * verde ensina a ignorar a seta.
 */
export function Variacao({
  pct,
  favoravelSeSobe = false,
}: {
  pct: number | null;
  favoravelSeSobe?: boolean;
}) {
  if (pct === null || !Number.isFinite(pct)) return <span className="text-tinta-fraca">—</span>;
  if (Math.abs(pct) < 1) return <span className="text-tinta-fraca">estável</span>;

  const sobe = pct > 0;
  const bom = sobe === favoravelSeSobe;
  return (
    <span className={bom ? "text-ok-tinta" : "text-risco-tinta"}>
      {sobe ? "▲" : "▼"} {Math.abs(pct).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
    </span>
  );
}

/** Barra de consumo. A referência de 100% aparece mesmo quando se estoura. */
export function BarraConsumo({ pct, tom }: { pct: number; tom: Tom }) {
  const cor =
    tom === "risco" ? "bg-risco-tinta" : tom === "atencao" ? "bg-atencao-tinta" : "bg-ok-tinta";
  return (
    <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-superficie-2">
      <div
        className={`h-full rounded-full ${cor}`}
        style={{ width: `${Math.min(Math.max(pct, 0), 100)}%` }}
      />
    </div>
  );
}

/** Tooltip de gráfico em div própria (§3.6), nunca o padrão do Recharts. */
export function TooltipRico({
  titulo,
  itens,
}: {
  titulo: string;
  itens: { rotulo: string; valor: string; cor?: string }[];
}) {
  return (
    <div className="rounded-lg border border-linha bg-superficie px-3 py-2 text-[12.5px] shadow-lg">
      <p className="mb-1.5 font-semibold">{titulo}</p>
      <ul className="grid gap-1">
        {itens.map((i) => (
          <li key={i.rotulo} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-tinta-media">
              {i.cor && <span aria-hidden className="size-2 rounded-sm" style={{ background: i.cor }} />}
              {i.rotulo}
            </span>
            <span className="num font-semibold">{i.valor}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * As cores das séries, num lugar só.
 *
 * ⚠️ Estavam duplicadas em `VisaoFinanceiro` e `PainelGestor`, com listas
 * ligeiramente diferentes — o Gestor tinha `comprometido`, a Visão tinha
 * `positivo` e `negativo`. Duas paletas que deveriam ser uma é como a mesma
 * série aparece de cor diferente em duas telas sem ninguém notar.
 *
 * Cinza para o orçado e azul para o realizado é deliberado: o orçado é
 * referência, o realizado é o fato. O laranja fica só para a projeção, que é o
 * único traço que não aconteceu ainda.
 */
export const COR = {
  orcado: "#c3cbd9",
  realizado: "#183c77",
  comprometido: "#8ea0c4",
  forecast: "#ff9933",
  positivo: "#3b6d11",
  negativo: "#a32d2d",
} as const;

/**
 * Legenda de série, escrita à mão.
 *
 * O `<Legend>` do Recharts fica minúsculo e não acompanha a tipografia do resto
 * (§3.6 manda legenda própria no donut pelo mesmo motivo). Aqui vale igual.
 *
 * ⚠️ Fica FORA do contêiner de altura fixa do gráfico. Dentro, o
 * `ResponsiveContainer` ocupa os 100% da altura e empurra a legenda para fora —
 * foi assim que ela sumia no "Meu orçamento", cortada na borda do cartão.
 */
export function LegendaGrafico({ series }: { series: [string, string][] }) {
  return (
    <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-tinta-media">
      {series.map(([rotulo, cor]) => (
        <li key={rotulo} className="flex items-center gap-1.5">
          <span aria-hidden className="size-2.5 shrink-0 rounded-sm" style={{ background: cor }} />
          {rotulo}
        </li>
      ))}
    </ul>
  );
}

/**
 * Campo de competência que abre o calendário ao clicar em qualquer ponto.
 *
 * ⚠️ O `<input type="month">` do Chrome só abre o seletor pelo ícone — clicar no
 * texto não faz nada, e quem não percebe o ícone conclui que o campo não
 * funciona. `showPicker()` conserta isso.
 *
 * O `try` não é decoração: o método não existe em navegador antigo e **lança**
 * quando a chamada não vem de gesto do usuário. Falhar aqui não pode impedir o
 * clique de acontecer.
 */
/**
 * A barra de filtros do protótipo (`.filters`): cartão branco, 12px de raio,
 * respiro de 16/20, campos alinhados pela base e a legenda empurrada para a
 * direita.
 *
 * Por que é um cartão e não um seletor solto no cabeçalho: o controle que muda
 * TODA a tela merece peso visual proporcional ao efeito. Solto ao lado do
 * título, o campo de competência parecia decoração do cabeçalho — e a pessoa
 * lia o mês errado sem notar que podia trocá-lo.
 */
export function BarraFiltros({
  children,
  legenda,
  className = "",
}: {
  children: ReactNode;
  legenda?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`surface-card flex flex-wrap items-end gap-x-5 gap-y-4 px-5 py-4 ${className}`}
    >
      {children}
      {legenda && <div className="ml-auto">{legenda}</div>}
    </div>
  );
}

/** Um campo rotulado da barra de filtros (`.field` do protótipo). */
export function Campo({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-semibold tracking-[0.04em] text-tinta-fraca uppercase">
        {rotulo}
      </span>
      {children}
    </label>
  );
}

/**
 * A legenda de cores do protótipo (`.legend-row`).
 *
 * Ela não é enfeite: a tela pinta barra, célula e número por faixa de consumo,
 * e sem legenda cada pessoa inventa o próprio significado para o laranja. As
 * cinco entradas são as do protótipo, inclusive "sem orçamento cadastrado",
 * que é o caso do CC que gastou num mês sem linha orçada.
 */
export function LegendaEstados() {
  const itens: [string, string][] = [
    ["Dentro do orçamento", "var(--color-ok-tinta)"],
    ["Próximo do limite", "var(--color-atencao-tinta)"],
    ["Acima do limite", "var(--color-risco-tinta)"],
    ["Saldo disponível", "var(--color-azul-medio)"],
    ["Sem orçamento cadastrado", "var(--color-neutro-tinta)"],
  ];
  return (
    <ul className="flex flex-wrap gap-x-3.5 gap-y-1.5 text-[12px] text-tinta-fraca">
      {itens.map(([rotulo, cor]) => (
        <li key={rotulo} className="flex items-center gap-1.5">
          <span aria-hidden className="size-2.5 rounded-full" style={{ background: cor }} />
          {rotulo}
        </li>
      ))}
    </ul>
  );
}

export function CampoCompetencia({
  competencia,
  onMudar,
  className = "num cursor-pointer rounded-lg border border-linha bg-superficie px-3 py-1.5 text-[13px]",
}: {
  competencia: string;
  onMudar: (c: string) => void;
  /** O campo aparece no cabeçalho das telas e dentro de formulário, com
   *  tamanhos diferentes. O comportamento de abrir é que não pode divergir. */
  className?: string;
}) {
  return (
    <input
      type="month"
      aria-label="Competência"
      value={competencia.slice(0, 7)}
      onChange={(e) => onMudar(`${e.target.value}-01`)}
      onClick={(e) => {
        try {
          (e.currentTarget as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
        } catch {
          /* navegador sem suporte: o ícone continua funcionando */
        }
      }}
      className={className}
    />
  );
}
